// Calendar helpers for community events. Unlike the on-demand class calendar
// (components_v1/addToCalendar/utils.js), this uses the event's real start/end,
// timezone, physical or online location, and event-specific description.

import { community_event_add_to_calendar_event } from 'gtm/pushEvents'

const DEFAULT_DURATION_MS = 60 * 60 * 1000

const stripHtml = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const escapeICS = (value) =>
  String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')

const getHostNames = (hosts) =>
  (hosts || [])
    .map((host) => {
      if (!host) return null
      return (
        host.name ||
        [host.firstName || host.first_name, host.lastName || host.last_name]
          .filter(Boolean)
          .join(' ')
          .trim()
      )
    })
    .filter(Boolean)
    .join(', ')

export const buildEventLocation = (event = {}) => {
  const physical =
    event.fullAddress ||
    [event.venue, event.city, event.region, event.country]
      .filter(Boolean)
      .join(', ')

  return physical || event.meetingUrl || ''
}

export const getEventStartEnd = (event = {}) => {
  const start = new Date(event.startAt)
  if (Number.isNaN(start.getTime())) return null

  let end = event.endAt ? new Date(event.endAt) : null
  if (!end || Number.isNaN(end.getTime())) {
    end = new Date(start.getTime() + DEFAULT_DURATION_MS)
  }

  return { start, end }
}

const buildDescriptionLines = (event = {}) => {
  const lines = []
  const about = stripHtml(event.description)
  if (about) lines.push(about)

  const hostNames = getHostNames(event.hosts)
  if (hostNames) lines.push(`Hosted by: ${hostNames}`)

  if (event.meetingUrl) lines.push(`Join online: ${event.meetingUrl}`)
  if (event.lumaUrl) lines.push(`Event details: ${event.lumaUrl}`)

  return lines
}

const formatUTC = (date) => date.toISOString().replace(/[-:]|\.\d{3}/g, '')

const isValidTimeZone = (timeZone) => {
  if (!timeZone) return false
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format(new Date())
    return true
  } catch {
    return false
  }
}

// Wall-clock time in a given IANA timezone as YYYYMMDDTHHMMSS (no trailing Z).
// Used for Google's `dates` + `ctz`, and for ICS DTSTART/DTEND with TZID.
const formatFloatingInTimeZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value
      return acc
    }, {})

  return `${parts.year}${parts.month}${parts.day}T${parts.hour}${parts.minute}${parts.second}`
}

const getRfcOffset = (date, timeZone) => {
  const floating = formatFloatingInTimeZone(date, timeZone)
  const asUtc = Date.UTC(
    Number(floating.slice(0, 4)),
    Number(floating.slice(4, 6)) - 1,
    Number(floating.slice(6, 8)),
    Number(floating.slice(9, 11)),
    Number(floating.slice(11, 13)),
    Number(floating.slice(13, 15))
  )
  const offsetMinutes = Math.round((asUtc - date.getTime()) / 60000)
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const hours = String(Math.floor(abs / 60)).padStart(2, '0')
  const minutes = String(abs % 60).padStart(2, '0')
  return `${sign}${hours}:${minutes}`
}

// Outlook compose expects an ISO timestamp; include the venue offset so it
// is stored as 8:00 AM GMT-4 rather than converted from a naive UTC value.
const formatISOInTimeZone = (date, timeZone) => {
  if (!isValidTimeZone(timeZone)) return date.toISOString()
  const floating = formatFloatingInTimeZone(date, timeZone)
  const iso = `${floating.slice(0, 4)}-${floating.slice(4, 6)}-${floating.slice(
    6,
    8
  )}T${floating.slice(9, 11)}:${floating.slice(11, 13)}:${floating.slice(
    13,
    15
  )}`
  return `${iso}${getRfcOffset(date, timeZone)}`
}

const buildIcsDateLines = (start, end, timeZone) => {
  if (isValidTimeZone(timeZone)) {
    return [
      `DTSTART;TZID=${timeZone}:${formatFloatingInTimeZone(start, timeZone)}`,
      `DTEND;TZID=${timeZone}:${formatFloatingInTimeZone(end, timeZone)}`
    ]
  }
  return [`DTSTART:${formatUTC(start)}`, `DTEND:${formatUTC(end)}`]
}

const buildGoogleDates = (start, end, timeZone) => {
  if (isValidTimeZone(timeZone)) {
    return {
      dates: `${formatFloatingInTimeZone(
        start,
        timeZone
      )}/${formatFloatingInTimeZone(end, timeZone)}`,
      ctz: timeZone
    }
  }

  return { dates: `${formatUTC(start)}/${formatUTC(end)}`, ctz: null }
}

const pushGA4 = (event, calendarType) => {
  community_event_add_to_calendar_event({ event, calendarType })
}

export const openGoogleCalendar = (event = {}) => {
  const range = getEventStartEnd(event)
  if (!range) return

  const { dates, ctz } = buildGoogleDates(
    range.start,
    range.end,
    event.timezone
  )
  const params = new URLSearchParams({
    text: event.title || 'ALO Wellness Club Event',
    dates,
    details: buildDescriptionLines(event).join('\n'),
    location: buildEventLocation(event)
  })
  if (ctz) params.set('ctz', ctz)

  pushGA4(event, 'google')
  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&${params.toString()}`
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const openOutlookCalendar = (event = {}) => {
  const range = getEventStartEnd(event)
  if (!range) return

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title || 'ALO Wellness Club Event',
    startdt: formatISOInTimeZone(range.start, event.timezone),
    enddt: formatISOInTimeZone(range.end, event.timezone),
    body: buildDescriptionLines(event).join('\n'),
    location: buildEventLocation(event)
  })

  const url = `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`
  window.open(url, '_blank', 'noopener,noreferrer')
  pushGA4(event, 'outlook')
}

export const downloadICS = (event = {}) => {
  const range = getEventStartEnd(event)
  if (!range) return

  const now = new Date()
  const uid = `${event.id || Date.now()}@alomoves.com`
  const description = buildDescriptionLines(event).join('\n')
  const timeZone = isValidTimeZone(event.timezone) ? event.timezone : null

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Alo Moves//Community Events//EN',
    timeZone ? `X-WR-TIMEZONE:${timeZone}` : null,
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatUTC(now)}`,
    ...buildIcsDateLines(range.start, range.end, timeZone),
    `SUMMARY:${escapeICS(event.title)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    `LOCATION:${escapeICS(buildEventLocation(event))}`,
    event.lumaUrl ? `URL:${escapeICS(event.lumaUrl)}` : null,
    'BEGIN:VALARM',
    'TRIGGER:-PT1H',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ]
    .filter(Boolean)
    .join('\r\n')

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  const safeTitle = (event.title || 'event')
    .replace(/[^\w-]+/g, '-')
    .toLowerCase()

  link.href = url
  link.download = `alo-event-${safeTitle}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
  pushGA4(event, 'ical')
}
